import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import Logger from "../../services/Logger";
import AdminStoryService from "./AdminStoryService";
import AdminJigsawService from "../jigsaw/AdminJigsawService";
import StoryImageZoomDialog from "./StoryImageZoomDialog";
import StoryImageZoomViewport from "./StoryImageZoomViewport";
import { getAppCopyrightLine } from "../../config/appCopyright";
import { getStoryDisplayTitle, getStoryTemplateSummary } from "./storyDisplayUtils";

async function copyImageUrl(url) {
    if (!url) return false;
    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch (err) {
        Logger.error("Story image URL copy failed:", err);
        return false;
    }
}

function openStoryImageInNewWindow(url) {
    if (!url || typeof url !== "string") return;
    window.open(url.trim(), "_blank", "noopener,noreferrer");
}

function pickTrimmedUrl(obj, camel, snake) {
    if (!obj || typeof obj !== "object") return null;
    const a = obj[camel];
    const b = obj[snake];
    if (typeof a === "string" && a.trim()) return a.trim();
    if (typeof b === "string" && b.trim()) return b.trim();
    return null;
}

function pickTrimmedPrompt(obj) {
    if (!obj || typeof obj !== "object") return null;
    const a = obj.imagePrompt;
    const b = obj.image_prompt;
    if (typeof a === "string" && a.trim()) return a.trim();
    if (typeof b === "string" && b.trim()) return b.trim();
    return null;
}

function toPlayCount(v) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function playEmailsFromStory(story, camel, snake) {
    if (!story || typeof story !== "object") return [];
    const raw = story[camel] ?? story[snake];
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
        const email = typeof item === "string" && item.trim() ? item.trim() : "No email";
        if (seen.has(email)) continue;
        seen.add(email);
        out.push(email);
    }
    return out;
}

function PlayEmailList({ emails }) {
    if (!emails.length) return null;
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.15, mt: 0.5, ml: 0.25 }}>
            {emails.map((email) => (
                <Typography
                    key={email}
                    variant="caption"
                    color={email === "No email" ? "text.disabled" : "text.secondary"}
                    sx={{ wordBreak: "break-all" }}
                >
                    {email}
                </Typography>
            ))}
        </Box>
    );
}

export default function AdminStoryDetail({ storyId: storyIdProp, onClose } = {}) {
    const { storyId: storyIdFromRoute } = useParams();
    const storyId = storyIdProp || storyIdFromRoute;
    const navigate = useNavigate();
    const handleBack = () => {
        if (typeof onClose === "function") {
            onClose();
            return;
        }
        navigate(-1);
    };
    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [regenImageDialogOpen, setRegenImageDialogOpen] = useState(false);
    const [regenImageLoading, setRegenImageLoading] = useState(false);
    const [fullImageOpen, setFullImageOpen] = useState(false);
    const [genSlidingLoading, setGenSlidingLoading] = useState(false);
    const [ensureJigsawLoading, setEnsureJigsawLoading] = useState(false);
    const hasSlidingOrJigsaw = Boolean(story?.sliding || story?.jigsaw);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!storyId) return;
            setLoading(true);
            const result = await AdminStoryService.getById(storyId);
            if (cancelled) return;
            setLoading(false);
            if (result.success && result.data) {
                setStory(result.data);
            } else {
                setStory(null);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Story not found",
                });
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    const mainImageUrl = story ? pickTrimmedUrl(story, "imageUrl", "image_url") : null;
    const previewUrl = story ? pickTrimmedUrl(story, "imagePreviewUrl", "image_preview_url") : null;
    const icon400Url = story ? pickTrimmedUrl(story, "imageIcon400Url", "image_icon_400_url") : null;
    const icon200Url = story ? pickTrimmedUrl(story, "imageIcon200Url", "image_icon_200_url") : null;
    const imagePromptText = story ? pickTrimmedPrompt(story) : null;
    const hasSizedStoryImages = Boolean(previewUrl || icon400Url || icon200Url);
    const title = story ? getStoryDisplayTitle(story) : "";
    const templateSummary = React.useMemo(() => (story ? getStoryTemplateSummary(story) : null), [story]);
    const wordPuzzlePlayCount = story
        ? toPlayCount(story.wordPuzzlePlayCount ?? story.word_puzzle_play_count)
        : 0;
    const slidingPlayCount = story
        ? toPlayCount(story.slidingPlayCount ?? story.sliding_play_count)
        : 0;
    const jigsawPlayCount = story
        ? toPlayCount(story.jigsawPlayCount ?? story.jigsaw_play_count)
        : 0;
    const hasWordPuzzle = Boolean(
        story?.crossTpl ||
            story?.crosswordTpl ||
            story?.gridTpl ||
            story?.crosswordTplId ||
            story?.gridTplId
    );
    const hasSlidingPuzzle = Boolean(story?.sliding) || slidingPlayCount > 0;
    const hasJigsawPuzzle = Boolean(story?.jigsaw) || jigsawPlayCount > 0;
    const wordPuzzlePlayLabel = templateSummary?.puzzleKind
        ? `${templateSummary.puzzleKind} plays`
        : "Word puzzle plays";
    const wordPuzzlePlayEmails = story
        ? playEmailsFromStory(story, "wordPuzzlePlayEmails", "word_puzzle_play_emails")
        : [];
    const slidingPlayEmails = story
        ? playEmailsFromStory(story, "slidingPlayEmails", "sliding_play_emails")
        : [];
    const jigsawPlayEmails = story
        ? playEmailsFromStory(story, "jigsawPlayEmails", "jigsaw_play_emails")
        : [];

    const handleCopyImageUrl = async (url) => {
        const ok = await copyImageUrl(url);
        setSnackbar({
            open: true,
            severity: ok ? "success" : "error",
            message: ok ? "Image URL copied" : "Could not copy URL",
        });
    };

    const handleConfirmDelete = async (id) => {
        if (!id) return;
        setDeleting(true);
        try {
            const result = await AdminStoryService.deleteById(id);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            if (typeof onClose === "function") {
                onClose();
                return;
            }
            navigate("/admin/story");
        } catch (e) {
            Logger.error("AdminStoryDetail delete:", e);
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.response?.message || e?.message || "Delete failed",
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleConfirmRegenImage = async (id) => {
        if (!id) return;
        setRegenImageLoading(true);
        try {
            const result = await AdminStoryService.regenImage(id);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Regenerate image failed",
                });
                return;
            }
            if (result.data) {
                setStory(result.data);
            }
            setSnackbar({
                open: true,
                severity: "success",
                message: "Image replaced. Story text was not changed.",
            });
        } catch (e) {
            Logger.error("AdminStoryDetail regen image:", e);
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.response?.message || e?.message || "Regenerate image failed",
            });
        } finally {
            setRegenImageLoading(false);
        }
    };

    const handleGenerateSlidingJigsaw = async () => {
        if (!storyId) return;
        setGenSlidingLoading(true);
        try {
            const result = await AdminStoryService.genSlidingJigsaw(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Generate sliding/jigsaw failed",
                });
                return;
            }
            const d = result.data || {};
            const parts = [];
            if (d.slidingGenerated) parts.push(`Sliding (${d.slidingTemplatesCreated || 0} templates)`);
            if (d.jigsawGenerated) parts.push(`Jigsaw (${d.jigsawTemplatesCreated || 0} templates)`);
            setSnackbar({
                open: true,
                severity: "success",
                message: parts.length > 0
                    ? `Generated: ${parts.join(", ")}`
                    : "Already exists — nothing generated.",
            });
            // Reload story to update state
            const reloaded = await AdminStoryService.getById(storyId);
            if (reloaded.success && reloaded.data) {
                setStory(reloaded.data);
            }
        } catch (e) {
            Logger.error("AdminStoryDetail gen-sliding-jigsaw:", e);
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.response?.message || e?.message || "Generate sliding/jigsaw failed",
            });
        } finally {
            setGenSlidingLoading(false);
        }
    };

    const handleEnsureJigsawTemplates = async () => {
        if (!storyId || !story?.jigsaw) return;
        setEnsureJigsawLoading(true);
        try {
            const result = await AdminJigsawService.ensureStoryTemplates(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Ensure jigsaw templates failed",
                });
                return;
            }
            const created = result.data?.templatesCreated ?? 0;
            setSnackbar({
                open: true,
                severity: created > 0 ? "success" : "info",
                message:
                    created > 0
                        ? `Jigsaw: created ${created} missing template row${created !== 1 ? "s" : ""}.`
                        : "Jigsaw: no missing templates — nothing to add.",
            });
        } catch (e) {
            Logger.error("AdminStoryDetail ensure jigsaw:", e);
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.response?.message || e?.message || "Ensure jigsaw templates failed",
            });
        } finally {
            setEnsureJigsawLoading(false);
        }
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                p: 2,
                width: "100%",
                maxWidth: "none",
                boxSizing: "border-box",
            }}
        >
            <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
                Back
            </Button>

            {loading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !story ? null : (
                <>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 2,
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 0.5,
                                        mb: 0.5,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography variant="h5" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                                        {title}
                                    </Typography>
                                    <Tooltip title="Generate a new illustration and replace only the image">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label="Regenerate image"
                                                onClick={() => setRegenImageDialogOpen(true)}
                                                disabled={regenImageLoading || deleting}
                                                sx={{ flexShrink: 0, width: 44, height: 44, mt: -0.5, mr: -1 }}
                                            >
                                                {regenImageLoading ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <AutorenewIcon />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <StackChips row={story} />
                                {(hasWordPuzzle || hasSlidingPuzzle || hasJigsawPuzzle) ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.75,
                                            mt: 1,
                                        }}
                                    >
                                        {hasWordPuzzle ? (
                                            <Box>
                                                <Chip
                                                    size="small"
                                                    variant={wordPuzzlePlayCount > 0 ? "filled" : "outlined"}
                                                    color={wordPuzzlePlayCount > 0 ? "warning" : "default"}
                                                    label={`${wordPuzzlePlayLabel}: ${wordPuzzlePlayCount}`}
                                                />
                                                {wordPuzzlePlayCount > 0 ? (
                                                    <PlayEmailList emails={wordPuzzlePlayEmails} />
                                                ) : null}
                                            </Box>
                                        ) : null}
                                        {hasSlidingPuzzle ? (
                                            <Box>
                                                <Chip
                                                    size="small"
                                                    variant={slidingPlayCount > 0 ? "filled" : "outlined"}
                                                    color={slidingPlayCount > 0 ? "warning" : "default"}
                                                    label={`Sliding plays: ${slidingPlayCount}`}
                                                />
                                                {slidingPlayCount > 0 ? (
                                                    <PlayEmailList emails={slidingPlayEmails} />
                                                ) : null}
                                            </Box>
                                        ) : null}
                                        {hasJigsawPuzzle ? (
                                            <Box>
                                                <Chip
                                                    size="small"
                                                    variant={jigsawPlayCount > 0 ? "filled" : "outlined"}
                                                    color={jigsawPlayCount > 0 ? "warning" : "default"}
                                                    label={`Jigsaw plays: ${jigsawPlayCount}`}
                                                />
                                                {jigsawPlayCount > 0 ? (
                                                    <PlayEmailList emails={jigsawPlayEmails} />
                                                ) : null}
                                            </Box>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={deleting || regenImageLoading}
                                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, overflow: "hidden" }}>
                    <Box sx={{ width: "100%" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            Main illustration{" "}
                            <Typography component="span" variant="caption" color="text.disabled">
                                (imageUrl)
                            </Typography>
                        </Typography>
                        {mainImageUrl ? (
                            <Box sx={{ width: "100%" }}>
                                {!regenImageLoading ? (
                                    <Stack
                                        direction="row"
                                        spacing={0.25}
                                        justifyContent="flex-end"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <StoryImageOverlayActions
                                            imageUrl={mainImageUrl}
                                            label="main illustration"
                                            onCopy={handleCopyImageUrl}
                                            inline
                                        />
                                    </Stack>
                                ) : null}
                                <Box sx={{ position: "relative", width: "100%" }}>
                                    {regenImageLoading ? (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "rgba(0,0,0,0.35)",
                                                zIndex: 3,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <CircularProgress />
                                        </Box>
                                    ) : null}
                                    <StoryImageZoomViewport
                                        imageUrl={mainImageUrl}
                                        onExpand={() => setFullImageOpen(true)}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No main illustration yet.
                            </Typography>
                        )}
                    </Box>

                    {hasSizedStoryImages ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                gap: 2,
                                width: "100%",
                                mt: mainImageUrl ? 2 : 1.5,
                            }}
                        >
                            {previewUrl ? (
                                <StoryImageAssetRow
                                    label="Preview"
                                    imageUrl={previewUrl}
                                    variant="landscape"
                                    onCopy={handleCopyImageUrl}
                                />
                            ) : null}
                            {icon400Url ? (
                                <StoryImageAssetRow
                                    label="Icon 400"
                                    imageUrl={icon400Url}
                                    variant="square"
                                    onCopy={handleCopyImageUrl}
                                />
                            ) : null}
                            {icon200Url ? (
                                <StoryImageAssetRow
                                    label="Icon 200"
                                    imageUrl={icon200Url}
                                    variant="square"
                                    onCopy={handleCopyImageUrl}
                                />
                            ) : null}
                        </Box>
                    ) : null}

                    {imagePromptText ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            <strong>Image prompt:</strong> {imagePromptText}
                        </Typography>
                    ) : null}

                    {(mainImageUrl || previewUrl) &&
                    getAppCopyrightLine() ? (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", lineHeight: 1.35 }}
                            >
                                {getAppCopyrightLine()}
                            </Typography>
                        </Box>
                    ) : null}

                    <Divider sx={{ my: 2 }} />
                    {!hasSlidingOrJigsaw && (
                        <>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Generate Sliding & Jigsaw
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 640 }}>
                                Creates sliding and jigsaw puzzles from the story's illustration. Only generates for types that don't exist yet. Safe to call more than once.
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                                <Tooltip
                                    title={
                                        mainImageUrl
                                            ? "Generate sliding and jigsaw puzzles"
                                            : "Add a main illustration (imageUrl) before generating."
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                genSlidingLoading ? (
                                                    <CircularProgress color="inherit" size={18} />
                                                ) : (
                                                    <ViewWeekIcon />
                                                )
                                            }
                                            onClick={handleGenerateSlidingJigsaw}
                                            disabled={genSlidingLoading || !storyId || !mainImageUrl}
                                        >
                                            Generate sliding & jigsaw
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                    {hasSlidingOrJigsaw && (
                        <>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Sliding & Jigsaw
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {story.sliding && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                        <Chip size="small" label="Sliding" variant="outlined" />
                                        {story.sliding.title && (
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {story.sliding.title}
                                            </Typography>
                                        )}
                                        {story.sliding.state && (
                                            <Chip
                                                size="small"
                                                label={story.sliding.state}
                                                color={story.sliding.state === "PUBLISHED" ? "success" : "default"}
                                                variant="filled"
                                            />
                                        )}
                                        {story.sliding.publishedAt && (
                                            <Typography variant="caption" color="text.secondary">
                                                Published: {new Date(story.sliding.publishedAt).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                {story.jigsaw && (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Chip size="small" label="Jigsaw" variant="outlined" />
                                            {story.jigsaw.title && (
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {story.jigsaw.title}
                                                </Typography>
                                            )}
                                            {story.jigsaw.state && (
                                                <Chip
                                                    size="small"
                                                    label={story.jigsaw.state}
                                                    color={story.jigsaw.state === "PUBLISHED" ? "success" : "default"}
                                                    variant="filled"
                                                />
                                            )}
                                            {story.jigsaw.publishedAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Published: {new Date(story.jigsaw.publishedAt).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
                                            Adds missing jigsaw rows when new enabled reusable templates exist. Safe to
                                            call more than once.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleEnsureJigsawTemplates}
                                            disabled={ensureJigsawLoading || !storyId}
                                            startIcon={
                                                ensureJigsawLoading ? (
                                                    <CircularProgress color="inherit" size={18} />
                                                ) : null
                                            }
                                            sx={{ alignSelf: "flex-start", textTransform: "none" }}
                                        >
                                            {ensureJigsawLoading
                                                ? "Ensuring…"
                                                : "Ensure missing jigsaw templates"}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {templateSummary ? (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Template
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {templateSummary.puzzleKind}
                                {templateSummary.state ? ` · ${templateSummary.state}` : ""}
                                {templateSummary.type
                                    ? ` · ${templateSummary.type.replace(/_/g, " ").toLowerCase()}`
                                    : ""}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                    <strong>ID:</strong> {templateSummary.id ?? "—"}
                                </Typography>
                                {templateSummary.name ? (
                                    <Typography variant="body2">
                                        <strong>Name:</strong> {templateSummary.name}
                                    </Typography>
                                ) : null}
                                <Typography variant="body2">
                                    <strong>Title:</strong> {templateSummary.title ?? "—"}
                                </Typography>
                                {templateSummary.difficultyLevel || templateSummary.language ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {[templateSummary.difficultyLevel, templateSummary.language]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </Typography>
                                ) : null}
                                <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }} color="text.secondary">
                                    Tags
                                </Typography>
                                {templateSummary.tags.length > 0 ? (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                        {templateSummary.tags.map((tag, i) => (
                                            <Chip
                                                key={`${tag}-${i}`}
                                                size="small"
                                                variant="outlined"
                                                label={tag}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        —
                                    </Typography>
                                )}
                            </Box>
                        </>
                    ) : null}
                </Paper>
                </>
            )}

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete story?"
                message={[
                    "This permanently deletes the story record, the story image, all sliding puzzles, and all jigsaw puzzles.",
                    "Facebook and Instagram posts of this image are removed when post ids are known.",
                    "The crossword or word-search puzzle is not deleted. Scheduled jobs will not recreate this story or its sliding/jigsaw puzzles.",
                ]}
                targetName={title ? `'${title}'` : storyId ? `Story ${storyId}` : "This story"}
                targetId={storyId}
                callback={handleConfirmDelete}
                warningAlert="This action cannot be undone."
            />
            <ConfirmDialog
                open={regenImageDialogOpen}
                setOpen={setRegenImageDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration. Story text is not changed."
                targetName={title ? `'${title}'` : storyId ? `Story ${storyId}` : "This story"}
                targetId={storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <StoryImageZoomDialog
                open={fullImageOpen}
                imageUrl={mainImageUrl || ""}
                title={title || "Story image"}
                onClose={() => setFullImageOpen(false)}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}

/** Open-in-new-tab and copy controls for a story image. */
function StoryImageOverlayActions({ imageUrl, label, onCopy, inline = false, sx = {} }) {
    if (!imageUrl) return null;

    return (
        <Box
            sx={
                inline
                    ? { display: "flex", gap: 0.25, ...sx }
                    : {
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 2,
                          display: "flex",
                          gap: 0.25,
                          ...sx,
                      }
            }
        >
            <Tooltip title={`Copy ${label} URL`}>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCopy?.(imageUrl);
                    }}
                    aria-label={`Copy ${label} URL`}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={`Open ${label} in new tab`} placement="left">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        openStoryImageInNewWindow(imageUrl);
                    }}
                    aria-label={`Open ${label} in new tab`}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

/** Fixed thumbnail height for preview + icon URLs shown under main illustration. */
const STORY_ASSET_THUMB_HEIGHT_PX = 100;

/** One labeled thumbnail column inside the horizontal asset row. */
function StoryImageAssetRow({ label, imageUrl, variant = "square", onCopy }) {
    if (!imageUrl) return null;
    const isLandscape = variant === "landscape";
    const h = STORY_ASSET_THUMB_HEIGHT_PX;
    const columnSx = isLandscape
        ? {
              flex: "1 1 160px",
              minWidth: 140,
              maxWidth: { xs: "100%", sm: 280 },
          }
        : {
              flex: "0 0 auto",
              width: h,
          };

    const frameSx = {
        position: "relative",
        width: "100%",
        height: h,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: isLandscape ? "action.hover" : "background.paper",
        overflow: "hidden",
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ...columnSx }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {label}
            </Typography>
            <Box sx={frameSx} aria-label={`${label} story image`}>
                <StoryImageOverlayActions imageUrl={imageUrl} label={label} onCopy={onCopy} />
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        objectPosition: "center",
                        display: "block",
                        boxSizing: "border-box",
                    }}
                />
            </Box>
        </Box>
    );
}

function StackChips({ row }) {
    const idStr =
        typeof row.id === "string"
            ? row.id.trim()
            : row.id != null && String(row.id).trim()
              ? String(row.id).trim()
              : "";
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, alignItems: "flex-start", maxWidth: "100%" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {row.language ? (
                    <Chip size="small" variant="outlined" label={`Language: ${row.language}`} />
                ) : null}
                {row.gridTplId ? <Chip size="small" variant="outlined" label="Grid template" /> : null}
                {row.crosswordTplId ? <Chip size="small" variant="outlined" label="Crossword template" /> : null}
            </Box>
            {idStr ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        wordBreak: "break-all",
                        fontFamily: "ui-monospace, monospace",
                        maxWidth: "100%",
                    }}
                >
                    ID: {idStr}
                </Typography>
            ) : null}
        </Box>
    );
}
